// frontend/src/contexts/GuideContext.js
//
// Orchestrates the authenticated user's FIRST-RUN MODAL QUEUE. It owns three
// dialogs and, above all, the decision of which one may open on a given login:
//
//   login 1  the WELCOME dialog - "what do you do?" - for a trialing user with
//            no profession (utils/onboardingSelectors.js::shouldShowWelcome).
//            Its optional last step is the "how did you hear about us" survey
//            while the server still owes it (`source_survey_due`).
//   login 2  the feature TOUR, as before.
//   next     the STANDALONE source survey, for a user the server still owes
//            the question who never reached the welcome's step
//            (onboardingSelectors.js::shouldShowSourceSurvey). Never in the
//            same session as the welcome or the auto-opened tour, and it waits
//            for the cookie banner like the welcome does.
//   then     the push / labels announcements, which wait on the guide
//            version. They read `guideAutoOpened` from this context to stand
//            down, and that flag is ALSO raised for the whole of a session the
//            survey takes (or is about to take) - which is how the survey
//            queues ahead of them without either file knowing it exists.
//
// WHY THE DECISION LIVES HERE AND NOT IN TWO PLACES. Both dialogs are modal and
// stacking them reads as an interruption rather than as onboarding, so exactly
// one of them may act per session. Keeping the welcome-vs-tour branch in ONE
// file - next to `decideAutoOpen`, which already encodes the rest of the queue -
// is what makes that a property of the code rather than a rule two components
// have to remember about each other.
//
// Both dialogs are lazy-loaded and mounted only while open, so neither chunk is
// in the initial bundle. Mounted inside AuthLayout (the authenticated shell), so
// nothing here runs on a public page.

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from "react";

import { useAuth } from "./AuthContext";
import { GUIDE_STEPS, CURRENT_GUIDE_VERSION } from "../content/guideSteps";
import { selectVisibleSteps, decideAutoOpen } from "../utils/guideSelectors";
import {
  isSourceSurveyDue,
  shouldShowSourceSurvey,
  shouldShowWelcome,
} from "../utils/onboardingSelectors";
import { sourceSurveyShownThisSession } from "../components/onboarding/SourceSurveySession";
import { isConsentRequired, CONSENT_SAVED_EVENT } from "../components/CookieConsent/consentUtils";
import { holdInstallBanner } from "../utils/pwaInstall";
import GuideErrorBoundary from "../components/guide/GuideErrorBoundary";

const FeatureGuideDialog = lazy(() => import("../components/guide/FeatureGuideDialog"));
const WelcomeDialog = lazy(() => import("../components/onboarding/WelcomeDialog"));
const SourceSurveyDialog = lazy(() => import("../components/onboarding/SourceSurveyDialog"));

// Tolerant default: the public navbar renders outside this provider, so
// useGuide() must not throw there — it simply reports "no guide available".
// `guideAutoOpened` lets a co-mounted announcement (e.g. PushNotificationsAnnounce)
// avoid stacking itself on top of a tour that auto-opened this session. The
// announcements ALSO stand down for the whole first session via the guide
// version itself (see shouldShowPushAnnounce): the tour owns login 2, they come
// later. `tourOpen` is the LIVE "the tour dialog is on screen right now" -
// opened automatically OR by hand - for a dashboard card that must not ask for
// anything underneath it (the first-steps card holds while it is true).
const GuideContext = createContext({
  openGuide: () => {},
  openWelcome: () => {},
  hasGuide: false,
  guideAutoOpened: false,
  tourOpen: false,
});

export const useGuide = () => useContext(GuideContext);

// Survives AuthLayout remounts (it remounts per route) and re-triggers if the
// guide version is bumped. Scoped to the session so a refresh re-evaluates.
// Written on BOTH auto-open decisions ('open' and the invisible 'mark-seen'),
// never on 'skip' — so "this key is set" means "the guide took its turn this
// session", which is exactly what a co-mounted promo needs to know.
const autoOpenKey = `leadify_guide_autoopened_v${CURRENT_GUIDE_VERSION}`;

// Separate session marker for "the tour was actually SHOWN via auto-open".
// Distinct from autoOpenKey (which also covers the invisible 'mark-seen'
// decision) because only a VISIBLE tour should suppress the promo modal.
const autoShownKey = `leadify_guide_autoshown_v${CURRENT_GUIDE_VERSION}`;

// "The welcome dialog took this session's modal slot." Written when it opens.
const welcomeShownKey = "leadify_welcome_shown_v1";

/**
 * Did the guide already take its auto-open turn this session (shown OR
 * silently marked seen)? Exported so the announce dialogs read the SAME key
 * rather than duplicating the string; the key name has one owner: this file.
 */
export const guideActedThisSession = () => {
  try {
    return sessionStorage.getItem(autoOpenKey) === "1";
  } catch {
    return true; // storage blocked - fail closed, never stack a second modal
  }
};

const tourAutoShownThisSession = () => {
  try {
    return sessionStorage.getItem(autoShownKey) === "1";
  } catch {
    return false; // sessionStorage unavailable — fall back to per-mount state
  }
};

/**
 * Has the welcome dialog already been shown this session?
 *
 * FAILS CLOSED (true on a storage error), the `guideActedThisSession` shape: an
 * unreadable sessionStorage must never let the tour take a slot the welcome
 * dialog may already have used, because the cost of being wrong that way is two
 * modals on one login.
 *
 * @returns {boolean}
 */
export const welcomeShownThisSession = () => {
  try {
    return sessionStorage.getItem(welcomeShownKey) === "1";
  } catch {
    return true;
  }
};

/**
 * Everything the survey's queue rule needs to know about THIS session, read
 * fresh. Each read fails closed on a storage error (see the helpers).
 *
 * @returns {object} the `session` argument of `shouldShowSourceSurvey`.
 */
const sourceSurveySessionSnapshot = () => ({
  currentGuideVersion: CURRENT_GUIDE_VERSION,
  welcomeShownThisSession: welcomeShownThisSession(),
  tourShownThisSession: tourAutoShownThisSession(),
  surveyShownThisSession: sourceSurveyShownThisSession(),
});

export const GuideProvider = ({ isHebrew, children }) => {
  const { user, isAuthenticated, loading, markGuideSeen } = useAuth();
  const [open, setOpen] = useState(false);
  const [welcome, setWelcome] = useState(null);
  const [surveyOpen, setSurveyOpen] = useState(false);
  // The id of the account the standalone survey was opened for on this mount
  // (null = not yet). Keyed by ACCOUNT, not a plain boolean (F7): a second
  // account signed in on the same mount is checked afresh.
  const surveyCheckedFor = useRef(null);
  // `openWelcome` is a stable callback handed to the first-steps card; it reads
  // the CURRENT user through this ref rather than re-binding on every refresh.
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  // True once the tour AUTO-opens this session (not via manual openGuide()).
  // A co-mounted promo modal reads this to avoid stacking on top of the tour
  // (its per-mount half; guideActedThisSession() is the cross-mount half).
  // Seeded from sessionStorage: every route mounts its own AuthLayout →
  // GuideProvider, so plain state would reset on navigation and the modal
  // would pop up right after the user finished the tour (review finding,
  // 2026-07-07). The session marker makes the stand-down survive remounts.
  const [guideAutoOpened, setGuideAutoOpened] = useState(tourAutoShownThisSession);
  const autoOpenChecked = useRef(false);
  const welcomeChecked = useRef(false);
  // Re-run the consent gate when the banner is answered, without polling.
  const [consentTick, setConsentTick] = useState(0);

  const visibleSteps = useMemo(() => selectVisibleSteps(GUIDE_STEPS, user), [user]);
  // Only offer the guide to a signed-in user — AuthLayout also wraps a couple
  // of non-protected routes (e.g. /accessibility), so guard on auth here.
  const hasGuide = Boolean(isAuthenticated && user) && visibleSteps.length > 0;

  const openGuide = useCallback(() => {
    if (hasGuide) setOpen(true);
  }, [hasGuide]);

  /**
   * Reopen the welcome dialog on demand - the first-steps card's CTAs.
   *
   * @param {{step?: "intro"|"push"}} [options]
   */
  const openWelcome = useCallback((options = {}) => {
    const step = options.step === "push" ? "push" : "intro";
    // The push CTA never carries the survey. An intro reopen may, but only if
    // the question is still owed and nothing has used it this session - not
    // the survey itself, and not the auto-opened tour.
    const askSource =
      step === "intro" &&
      isSourceSurveyDue(userRef.current) &&
      !sourceSurveyShownThisSession() &&
      !tourAutoShownThisSession();
    // A reopened welcome takes the session's first-run slot too, so the
    // standalone survey does not follow it the moment it closes.
    //
    // DELIBERATE side effect: the same key is the tour's `welcomeOwnsSession`
    // input, so a reopen (incl. the push CTA) also stands the auto-tour down
    // for the rest of this session. One onboarding surface per session is the
    // rule; in practice the tour has already decided on load before any
    // first-steps CTA can be clicked, so this only closes a theoretical race.
    try {
      sessionStorage.setItem(welcomeShownKey, "1");
    } catch {
      /* the survey effect's own `welcome` guard still covers this mount */
    }
    setWelcome({ step, surface: "reopen", askSource });
  }, []);

  const closeSurvey = useCallback(() => setSurveyOpen(false), []);

  const closeWelcome = useCallback(() => setWelcome(null), []);

  // Dismiss (X / skip / finish) — records the version so it won't auto-open
  // again until CURRENT_GUIDE_VERSION increases.
  const closeGuide = useCallback(() => {
    setOpen(false);
    if ((user?.onboarding_guide_version || 0) < CURRENT_GUIDE_VERSION) {
      markGuideSeen(CURRENT_GUIDE_VERSION);
    }
  }, [user, markGuideSeen]);

  // The cookie banner is `position: fixed; zIndex: 9999`, ABOVE MUI's dialog
  // layer - on a phone it covers the dialog's action row outright. So the
  // welcome dialog waits for the banner to be answered, and this listener is
  // how it learns that happened without polling localStorage on a timer.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const bump = () => setConsentTick((n) => n + 1);
    window.addEventListener(CONSENT_SAVED_EVENT, bump);
    return () => window.removeEventListener(CONSENT_SAVED_EVENT, bump);
  }, []);

  // The welcome dialog's own auto-open. Checked BEFORE the tour's, and the tour
  // is told to stand down for the whole session either way (see
  // `welcomeOwnsSession` below).
  useEffect(() => {
    if (welcomeChecked.current) return;
    if (loading || !isAuthenticated || !user) return;
    if (!shouldShowWelcome(user)) return;
    if (welcomeShownThisSession()) return;
    // Consent first. `consentTick` is in the dependency list so answering the
    // banner re-runs this effect rather than leaving the dialog suppressed for
    // the rest of the session.
    if (isConsentRequired()) return;

    welcomeChecked.current = true;
    try {
      sessionStorage.setItem(welcomeShownKey, "1");
    } catch {
      /* the ref guard still prevents a re-open on this mount */
    }
    setWelcome({
      step: "intro",
      surface: "login",
      // Snapshot ONCE, at opening: a refresh mid-run must not add or drop the
      // last step under the user's feet.
      askSource: isSourceSurveyDue(user) && !sourceSurveyShownThisSession(),
    });
  }, [loading, isAuthenticated, user, consentTick]);

  // First-login (or post-bump) auto-open for the TOUR.
  useEffect(() => {
    if (autoOpenChecked.current) return;

    let alreadyAutoOpened = false;
    try {
      alreadyAutoOpened = sessionStorage.getItem(autoOpenKey) === "1";
    } catch {
      // sessionStorage unavailable (private mode / blocked) — treat as not-yet.
    }

    const decision = decideAutoOpen({
      loading,
      isAuthenticated,
      user,
      visibleStepsCount: visibleSteps.length,
      alreadyAutoOpened,
      currentVersion: CURRENT_GUIDE_VERSION,
      // BOTH halves, and the second is the load-bearing one: closing the dialog
      // stamps `onboarding_welcome_seen_at` optimistically, so `shouldShowWelcome`
      // flips to false in the same tick and would hand the tour the rest of
      // login 1.
      welcomeOwnsSession: shouldShowWelcome(user) || welcomeShownThisSession(),
    });
    if (decision === "skip") return;

    // We've decided to act — guard against re-running (this mount + this session).
    autoOpenChecked.current = true;
    try {
      sessionStorage.setItem(autoOpenKey, "1");
    } catch {
      /* ignore storage failure — the ref guard still prevents re-open this mount */
    }

    if (decision === "mark-seen") {
      // Nothing to show for this user; record it so we don't re-check forever.
      markGuideSeen(CURRENT_GUIDE_VERSION);
    } else {
      setOpen(true);
      setGuideAutoOpened(true); // signal co-mounted promos to stand down
      try {
        sessionStorage.setItem(autoShownKey, "1"); // …and keep them down all session
      } catch {
        /* ignore — the state flag still covers this mount */
      }
    }
  }, [loading, isAuthenticated, user, visibleSteps, markGuideSeen]);

  // The STANDALONE source survey. Declared AFTER the welcome and tour effects
  // on purpose: in the commit where either of them opens, it has already
  // written its session key, so this one reads "taken" and stands down.
  useEffect(() => {
    if (loading || !isAuthenticated || !user) return;
    if (surveyCheckedFor.current !== null && surveyCheckedFor.current === user.id) return;
    // Never on top of a dialog that is up right now (a manually opened tour
    // included - the session keys only cover the AUTO-opened one).
    if (welcome || open) return;
    if (!shouldShowSourceSurvey(user, sourceSurveySessionSnapshot())) return;
    // The cookie banner sits above MUI's dialog layer; wait for it, as the
    // welcome dialog does. `consentTick` re-runs this when it is answered.
    if (isConsentRequired()) return;

    // The session marker itself is written by the dialog when it mounts
    // (useSourceSurveyFlow.markShown), so a dialog chunk that fails to load
    // never claims the session; this ref covers the mount until then.
    surveyCheckedFor.current = user.id;
    setSurveyOpen(true);
  }, [loading, isAuthenticated, user, consentTick, welcome, open]);

  /**
   * Does the survey own (or is it about to take) this session's modal slot?
   *
   * Computed DURING RENDER, not in an effect: the promo components are this
   * provider's CHILDREN, and a child's effects run before its parent's - so a
   * flag raised by an effect here would arrive one commit after a promo had
   * already opened. Read in render, it is in the very context value the
   * promos' first effect sees.
   */
  const surveyOwnsSession =
    surveyOpen ||
    sourceSurveyShownThisSession() ||
    Boolean(
      !loading &&
        isAuthenticated &&
        user &&
        shouldShowSourceSurvey(user, sourceSurveySessionSnapshot())
    );

  // The adaptive install strip is `position: fixed` at the top of the page and
  // would sit over a fullScreen dialog on a phone. Held for the dialog's
  // lifetime - the PushNotificationsAnnounce precedent - and released by the
  // effect's own cleanup, so an unmount mid-flow can never leave it held.
  const firstRunModalUp = Boolean(welcome) || surveyOpen;
  useEffect(() => {
    if (!firstRunModalUp) return undefined;
    return holdInstallBanner();
  }, [firstRunModalUp]);

  // `guideAutoOpened` keeps its name for the promos that read it, but what it
  // now says is "a first-run modal owns this session": the auto-opened tour,
  // or the source survey.
  const promosStandDown = guideAutoOpened || surveyOwnsSession;
  const value = useMemo(
    () => ({
      openGuide,
      openWelcome,
      hasGuide,
      guideAutoOpened: promosStandDown,
      // The tour dialog itself, while it is mounted (auto-opened or manual).
      tourOpen: open,
    }),
    [openGuide, openWelcome, hasGuide, promosStandDown, open]
  );

  return (
    <GuideContext.Provider value={value}>
      {children}
      {welcome && (
        <GuideErrorBoundary onError={closeWelcome}>
          <Suspense fallback={null}>
            <WelcomeDialog
              open
              isHebrew={isHebrew}
              initialStep={welcome.step}
              surface={welcome.surface}
              askSource={Boolean(welcome.askSource)}
              onClose={closeWelcome}
            />
          </Suspense>
        </GuideErrorBoundary>
      )}
      {surveyOpen && (
        <GuideErrorBoundary onError={closeSurvey}>
          <Suspense fallback={null}>
            <SourceSurveyDialog open isHebrew={isHebrew} onClose={closeSurvey} />
          </Suspense>
        </GuideErrorBoundary>
      )}
      {open && (
        <GuideErrorBoundary onError={() => setOpen(false)}>
          <Suspense fallback={null}>
            <FeatureGuideDialog
              open={open}
              steps={visibleSteps}
              isHebrew={isHebrew}
              onClose={closeGuide}
            />
          </Suspense>
        </GuideErrorBoundary>
      )}
    </GuideContext.Provider>
  );
};
